import {
  ColumnFactory,
  createIconCellFormatter,
  createSelectionColumn,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  ExplorerStyledDisplayGrid,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  SelectionProviderChildProps,
  TypedBodyCellProps,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import { BodyCell } from '@tableau/widgets-datagrid';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';

import { FieldDataType, FieldOrderField } from '../../../gen-src/queries/globalTypes';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import {
  createQueryPreservingReplacingLinkCellFormatter,
} from '../datagrid/cellFormatters/QueryPreservingLinkCellFormatter';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { FieldIcon } from '../field/types/FieldIcon';
import { DescriptionIconFromRowData, DescriptionInheritedType, DescriptionTextFromRowData, RowDataType } from '../fieldColumn/DescriptionInfo';
import { getNameWithApplicableDataType } from '../sharedDisplayGrid/FieldsDisplayGrid';
import { FieldsValidSortFields } from '../sorting/sortProviders/FieldsGridSortProvider';

export interface WorkbookFieldsDisplayGridRowData {
  readonly workbookId: string;
  readonly name: string | null;
  readonly dataType: FieldDataType | null;
  readonly id: string;
  readonly description?: string | null;
  readonly descriptionInherited?: (DescriptionInheritedType | null)[] | null;
  readonly fieldIcon: FieldIcon | null;
  readonly sheetCount: number | null;
}

type WorkbookFieldsColumnFactory = ColumnFactory<
  GridSortProps<FieldsValidSortFields>,
  WorkbookFieldsDisplayGridRowData,
  FieldsValidSortFields
>;

const columns: ReadonlyArray<WorkbookFieldsColumnFactory> = [
  () => createSelectionColumn(),
  (sortParams) => ({
    name: 'type',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderType(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createIconCellFormatter(
      ({ fieldIcon }) => <SvgFieldIcon fieldIcon={fieldIcon} />,
      ({ fieldIcon }) => fieldIcon ? FieldNameMappings[fieldIcon]() : ''
    ),
    sortable: false,
    resizable: false
  }),
  (sortParams) => ({
    name: 'name',
    dataKey: FieldOrderField.NAME,
    headerText: Messages.GridHeaderName(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createQueryPreservingReplacingLinkCellFormatter<WorkbookFieldsDisplayGridRowData>(
      getNameWithApplicableDataType,
      (urls, { id, workbookId }) => urls.workbookLineageFieldPath(workbookId, id),
    ),
    sortable: true,
  }),
  (sortParams) => ({
    name: 'sheets',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderSheets(),
    headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
    cellFormatter: createNumberCellFormatter(({ sheetCount }) => sheetCount),
    sortable: false,
  }),
  (sortParams) => ({
    name: 'description',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderDescription(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: descriptionCellFormatter(),
    sortable: false,
    growToFit: true,
  }),
];

export type WorkbookFieldsDisplayGridProps =
  UsualDisplayGridProps<WorkbookFieldsDisplayGridRowData, GraphQLError> &
  SelectionProviderChildProps &
  GridSortProps<FieldsValidSortFields>;

export function WorkbookFieldsDisplayGrid(props: WorkbookFieldsDisplayGridProps): JSX.Element {
  const cols = columns.map(columnFactory => columnFactory(props));

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      selectedKeys={props.selectedKeys}
      updateSelectionForKey={props.updateSelectionForKey}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}

// Returns a cell formatter that displays a description and, if applicable, a description with inheritance info
function descriptionCellFormatter(): React.Factory<BodyCell.Props> & React.Factory<TypedBodyCellProps<WorkbookFieldsDisplayGridRowData>> {
  return createTextCellFormatter(
    descriptionTextFromRowData,
    Messages.NoDescriptionGridPlaceholder(), // placeholder
    () => false, // getDisabledFromRowData (always enabled)
    false, // noPadding
    descriptionIconFromRowData,
  );
}

// Call the helper methods with only the needed fields. I can't call the helper methods
// with a broader row-data type because createTextCellFormatter mandates that the interfaces
// of the helper methods accept the same broad type.
function minifyRowData(rowData: WorkbookFieldsDisplayGridRowData): RowDataType {
  return {
    description: rowData.description,
    descriptionInherited: rowData.descriptionInherited,
  };
}

function descriptionTextFromRowData(rowData: WorkbookFieldsDisplayGridRowData) {
  return DescriptionTextFromRowData(minifyRowData(rowData));
}

function descriptionIconFromRowData(rowData: WorkbookFieldsDisplayGridRowData) {
  return DescriptionIconFromRowData(minifyRowData(rowData));
}
